import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { API, restoreEntitlement } from "../lib/api";
import { useSiteContent } from "../lib/siteContent";
import { SiteHeader } from "../components/SiteHeader";
import { SomaticTexture } from "../components/SomaticTexture";
import { ArrowLeft, Lock, Loader2, KeyRound, BookLock, Headphones, Feather, GalleryHorizontal, Download, X, BookOpen } from "lucide-react";

const SERIF = '"Cormorant Garamond", Georgia, serif';
const SANS = '"Plus Jakarta Sans", system-ui, sans-serif';

const readEntitlement = () => {
  try { return JSON.parse(localStorage.getItem("bnm-entitlement") || "{}"); }
  catch { return {}; }
};
const hasVaultAccess = () => (readEntitlement().tiers || []).some((t) => t === "vault_member");

export default function Vault() {
  const site = useSiteContent();
  const [vault, setVault] = useState(hasVaultAccess());
  const [accessCode, setAccessCode] = useState(() => readEntitlement().access_code || "");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(hasVaultAccess());
  const [err, setErr] = useState("");
  const [code, setCode] = useState("");
  const [unlockErr, setUnlockErr] = useState("");
  const [openEntry, setOpenEntry] = useState(null);

  const load = useCallback(async (ac) => {
    if (!ac) return;
    setLoading(true); setErr("");
    try {
      const { data } = await axios.get(`${API}/vault`, { params: { code: ac } });
      setEntries(data.entries || []);
    } catch (e) {
      if (e.response?.status === 403) { setVault(false); }
      else setErr("We couldn't open the Vault just now. Please try again.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (vault && accessCode) load(accessCode); }, [vault, accessCode, load]);

  // Admin preview / email links: "?code=" unlocks, "?fresh=1" forces the locked gate.
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("fresh") === "1") {
        localStorage.removeItem("bnm-entitlement");
        setVault(false); setAccessCode("");
        window.history.replaceState(null, "", window.location.pathname);
        return;
      }
      const c = sp.get("code");
      if (c) {
        restoreEntitlement(c).then((data) => {
          if ((data.tiers || []).includes("vault_member")) {
            localStorage.setItem("bnm-entitlement", JSON.stringify(data));
            setAccessCode(data.access_code); setVault(true);
          }
          window.history.replaceState(null, "", window.location.pathname);
        }).catch(() => {});
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unlock = async (e) => {
    e.preventDefault(); setUnlockErr("");
    try {
      const data = await restoreEntitlement(code);
      if ((data.tiers || []).includes("vault_member")) {
        localStorage.setItem("bnm-entitlement", JSON.stringify(data));
        setAccessCode(data.access_code);
        setVault(true);
      } else { setUnlockErr("That code doesn't include the Language Vault."); }
    } catch (err) { setUnlockErr(err?.message || "That code wasn't found."); }
  };

  const reframes = useMemo(() => entries.filter((e) => e.section === "reframe"), [entries]);
  const reflections = useMemo(() => entries.filter((e) => e.section === "reflection"), [entries]);
  const audioUrl = (id) => `${API}/vault/audio/${id}?code=${encodeURIComponent(accessCode)}`;
  const keepsakeUrl = (id, dl) => `${API}/vault/keepsake/${id}?code=${encodeURIComponent(accessCode)}${dl ? "&dl=1" : ""}`;

  const EntryCard = ({ e, i }) => {
    const blurb = (e.body || "").replace(/\s+/g, " ").trim();
    const short = blurb.length > 150 ? `${blurb.slice(0, 150).trimEnd()}…` : blurb;
    return (
      <motion.article
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.4), ease: [0.22, 1, 0.36, 1] }}
        data-testid={`vault-entry-${e.id}`}
        className="group relative flex flex-col overflow-hidden rounded-sm"
        style={{ background: "#12151C", border: "1px solid rgba(212,163,115,0.14)" }}
      >
        <button type="button" onClick={() => setOpenEntry(e)} data-testid={`vault-entry-open-${e.id}`} className="flex flex-col text-left focus:outline-none">
          <div className="relative aspect-[4/3] w-full overflow-hidden">
            <img
              src={keepsakeUrl(e.id)}
              alt={`The art plate for ${e.title}`}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              data-testid={`vault-thumb-${e.id}`}
            />
            <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(to top, rgba(12,14,18,0.85), transparent 58%)" }} />
            {e.has_audio && (
              <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wide"
                style={{ background: "rgba(12,14,18,0.6)", color: "#D4A373", border: "1px solid rgba(212,163,115,0.22)" }}>
                <Headphones className="h-3 w-3" /> Audio
              </span>
            )}
          </div>
          <div className="p-6 sm:p-7">
            {e.month && (
              <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "#98A898" }}>
                {e.month} · Plate {String(e.plate_index).padStart(2, "0")}
              </p>
            )}
            <h3 className="mt-2 text-2xl leading-tight" style={{ fontFamily: SERIF, color: "#F7F5F0" }}>{e.title}</h3>
            {short && <p className="mt-2.5 text-[14px] leading-relaxed" style={{ color: "#A3A8B2" }}>{short}</p>}
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium transition-opacity group-hover:opacity-80" style={{ color: "#D4A373" }}>
              <BookOpen className="h-4 w-4" /> Read the full entry
            </span>
          </div>
        </button>
      </motion.article>
    );
  };

  const EntryModal = ({ e, onClose }) => (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      style={{ background: "rgba(6,7,10,0.82)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="vault-entry-modal"
    >
      <motion.article
        initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        onClick={(ev) => ev.stopPropagation()}
        className="relative my-4 w-full max-w-2xl overflow-hidden rounded-sm"
        style={{ background: "#12151C", border: "1px solid rgba(212,163,115,0.2)" }}
      >
        <button onClick={onClose} data-testid="vault-entry-modal-close"
          className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full transition-transform hover:scale-105"
          style={{ background: "rgba(12,14,18,0.6)", color: "#D4A373", border: "1px solid rgba(212,163,115,0.22)" }}>
          <X className="h-4 w-4" />
        </button>
        <div className="relative aspect-[16/9] w-full overflow-hidden">
          <img src={keepsakeUrl(e.id)} alt={`The art plate for ${e.title}`} className="h-full w-full object-cover" />
          <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(to top, rgba(18,21,28,0.92), transparent 52%)" }} />
        </div>
        <div className="p-7 sm:p-9">
          {e.month && <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "#98A898" }}>{e.month} · Plate {String(e.plate_index).padStart(2, "0")}</p>}
          <h3 className="mt-2 text-3xl leading-tight sm:text-4xl" style={{ fontFamily: SERIF, color: "#F7F5F0" }}>{e.title}</h3>
          {e.body && <p className="mt-4 whitespace-pre-wrap text-[15px] leading-relaxed" style={{ color: "#C7CBD2" }} data-testid="vault-entry-modal-body">{e.body}</p>}
          {e.has_audio && (
            <div className="mt-6">
              <p className="mb-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em]" style={{ color: "#D4A373" }}>
                <Headphones className="h-3.5 w-3.5" /> Listen
              </p>
              <audio controls preload="none" src={audioUrl(e.id)} data-testid={`vault-audio-${e.id}`} className="w-full" style={{ colorScheme: "dark" }} />
            </div>
          )}
          <div className="mt-6 flex items-center gap-4 border-t pt-5" style={{ borderColor: "rgba(212,163,115,0.14)" }}>
            <img src={keepsakeUrl(e.id)} alt="" loading="lazy" className="h-14 w-14 flex-none rounded-sm object-cover" style={{ border: "1px solid rgba(212,163,115,0.22)" }} />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color: "#98A898" }}>Your keepsake · Plate {String(e.plate_index).padStart(2, "0")}</p>
              <a href={keepsakeUrl(e.id, true)} download data-testid={`vault-keepsake-${e.id}`}
                className="mt-1.5 inline-flex items-center gap-1.5 text-sm transition-opacity hover:opacity-80" style={{ color: "#D4A373" }}>
                <Download className="h-3.5 w-3.5" /> Download this plate
              </a>
            </div>
          </div>
        </div>
      </motion.article>
    </motion.div>
  );

  const SectionHead = ({ icon: Icon, kicker, title }) => (
    <div className="mb-6">
      <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.35em]" style={{ color: "#98A898" }}>
        <Icon className="h-4 w-4" style={{ color: "#D4A373" }} /> {kicker}
      </p>
      <h2 className="mt-2 text-3xl sm:text-4xl" style={{ fontFamily: SERIF, color: "#F7F5F0", fontWeight: 500 }}>{title}</h2>
    </div>
  );

  return (
    <div className="relative min-h-[100dvh]" style={{ background: "#0C0E12", fontFamily: SANS }} data-testid="vault-page">
      <SomaticTexture />

      <SiteHeader theme="dark" right={<span className="hidden items-center gap-2 text-sm sm:inline-flex" style={{ color: "#D4A373" }}><BookLock className="h-4 w-4" /> The Language Vault</span>} />

      <section className="relative z-10 mx-auto max-w-5xl px-5 pb-10 pt-16 sm:px-8 sm:pt-24">
        <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-[46rem] max-w-[90vw] -translate-x-1/2"
          style={{ background: "radial-gradient(60% 100% at 50% 0%, rgba(212,163,115,0.16), transparent 70%)" }} />
        <p className="relative text-[11px] uppercase tracking-[0.35em]" style={{ color: "#98A898" }}>A members' room · ongoing restorative practice</p>
        <h1 data-testid="vault-hero-title" className="relative mt-4 text-5xl leading-[0.95] sm:text-6xl lg:text-7xl" style={{ fontFamily: SERIF, color: "#F7F5F0", fontWeight: 500 }}>
          The Language Vault
        </h1>
        <p className="relative mt-5 max-w-xl text-lg italic" style={{ fontFamily: SERIF, color: "#D4A373" }}>Monthly somatic reframes, reflections &amp; audio from Casey Muze.</p>
      </section>

      <section className="relative z-10 mx-auto max-w-3xl px-5 pb-6 sm:px-8" data-testid="vault-narrative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-sm p-8 sm:p-11"
          style={{ background: "#12151C", border: "1px solid rgba(212,163,115,0.14)" }}
        >
          <div className="pointer-events-none absolute inset-0 opacity-60"
            style={{ background: "radial-gradient(85% 55% at 0% 0%, rgba(212,163,115,0.08), transparent 70%)" }} />
          <p className="relative text-[11px] uppercase tracking-[0.35em]" style={{ color: "#98A898" }}>Reflect · Reframe · Return</p>
          <div className="relative mt-5 space-y-5" style={{ color: "#C7CBD2" }}>
            <p className="text-lg leading-relaxed sm:text-xl" style={{ fontFamily: SERIF, color: "#F7F5F0" }}>
              Picture a dimly lit room, shelves bowed with phrases, old sentences stacked like heirlooms, each one still carrying the weight of where it came from.
            </p>
            <p className="text-[15px] leading-relaxed">
              Words spoken in anger. Words whispered in grief. Words handed down at the dinner table. Words repeated so often they settled into your bones — until they began to sound like truth.
            </p>
            <p className="text-[15px] leading-relaxed">
              But not every belief you inherited was built to hold your healing. Some language keeps people bracing long after the danger has passed. Some words dress themselves up as identity while quietly locking the door from the inside.
            </p>
            <p className="text-[15px] leading-relaxed italic" style={{ fontFamily: SERIF, color: "#D4A373" }}>
              This vault exists to turn on the light. Without performance. Without shame. Without hiding.
            </p>
            <p className="text-[15px] leading-relaxed">
              A transparent space to sort through the language tied to your emotions, your environment, your relationships, and your inner dialogue — and to decide what still belongs on the shelf, and what no longer deserves a home inside you.
            </p>
          </div>

          <div className="relative mt-8 grid gap-4 border-t pt-7 sm:grid-cols-3" style={{ borderColor: "rgba(212,163,115,0.14)" }}>
            {[
              { k: "Feel", v: "Less shame. More room to breathe." },
              { k: "Practice", v: "See how words shape healing." },
              { k: "Stay", v: "Keep gentle language close." },
            ].map((p) => (
              <div key={p.k} data-testid={`vault-pillar-${p.k.toLowerCase()}`}>
                <p className="text-sm uppercase tracking-[0.2em]" style={{ color: "#D4A373" }}>{p.k}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: "#A3A8B2" }}>{p.v}</p>
              </div>
            ))}
          </div>

          <p className="relative mt-8 text-sm italic" style={{ fontFamily: SERIF, color: "#98A898" }}>
            Return. Reflect. Reignite your work.
          </p>
        </motion.div>
      </section>

      {!vault ? (
        <section className="relative z-10 mx-auto max-w-2xl px-5 pb-28 sm:px-8" data-testid="vault-gate">
          <div className="relative overflow-hidden rounded-sm p-8 text-center sm:p-12" style={{ background: "#12151C", border: "1px solid rgba(212,163,115,0.2)" }}>
            <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(70% 60% at 50% 0%, rgba(212,163,115,0.12), transparent 70%)" }} />
            <span className="relative mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{ background: "rgba(212,163,115,0.12)", color: "#D4A373" }}><Lock className="h-5 w-5" /></span>
            <h2 className="relative mt-5 text-3xl" style={{ fontFamily: SERIF, color: "#F7F5F0" }} data-testid="vault-gate-heading">{site?.vault?.gate_heading || "This room is for Vault members"}</h2>
            <p className="relative mx-auto mt-3 max-w-md text-sm leading-relaxed" style={{ color: "#A3A8B2" }} data-testid="vault-gate-body">
              {site?.vault?.gate_body || "Inside: monthly somatic linguistic reframes, gentle reflections, and audio recordings from Casey — alongside the full art exhibition. $15 your first month, then $25/month. Cancel anytime."}
            </p>
            {(site?.vault?.gate_inside ?? "What's inside: monthly somatic reframes, reflections & audio — plus the complete art gallery.") && (
              <p className="relative mx-auto mt-3 inline-flex max-w-md items-center gap-2 rounded-full px-4 py-1.5 text-[12px] leading-relaxed" data-testid="vault-gate-inside"
                style={{ background: "rgba(212,163,115,0.12)", color: "#D4A373" }}>
                <BookLock className="h-3.5 w-3.5 flex-none" />
                {site?.vault?.gate_inside || "What's inside: monthly somatic reframes, reflections & audio — plus the complete art gallery."}
              </p>
            )}
            <Link to="/offerings/the-language-vault" data-testid="vault-join-button"
              className="relative mt-6 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-medium transition-transform hover:scale-[1.02]"
              style={{ background: "#D4A373", color: "#0C0E12" }}>
              Join the Vault
            </Link>
            <form onSubmit={unlock} className="relative mx-auto mt-6 flex max-w-xs items-center gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Member access code" data-testid="vault-passcode-input"
                className="min-w-0 flex-1 rounded-full border bg-transparent px-4 py-2 text-sm text-[#F7F5F0] placeholder:text-[#636A78] focus:outline-none"
                style={{ borderColor: "rgba(255,255,255,0.12)" }} />
              <button type="submit" data-testid="vault-unlock-submit" className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-xs" style={{ background: "rgba(255,255,255,0.08)", color: "#D4A373" }}><KeyRound className="h-3.5 w-3.5" /></button>
            </form>
            {unlockErr && <p className="relative mt-2 text-xs text-[#C88A75]" data-testid="vault-unlock-error">{unlockErr}</p>}
          </div>
        </section>
      ) : loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#D4A373]" /></div>
      ) : (
        <section className="relative z-10 mx-auto max-w-5xl px-5 pb-28 sm:px-8" data-testid="vault-content">
          {err && <p className="mb-6 text-sm text-[#C88A75]" data-testid="vault-error">{err}</p>}

          {entries.length === 0 && !err && (
            <div className="rounded-sm p-10 text-center" style={{ background: "#12151C", border: "1px solid rgba(212,163,115,0.14)" }}>
              <p className="text-sm" style={{ color: "#A3A8B2" }}>New reframes and reflections are being prepared. Check back soon.</p>
            </div>
          )}

          {reframes.length > 0 && (
            <div className="mb-16">
              <SectionHead icon={Feather} kicker="This month's practice" title="Somatic Reframes" />
              <div className="grid gap-5 sm:grid-cols-2">{reframes.map((e, i) => <EntryCard key={e.id} e={e} i={i} />)}</div>
            </div>
          )}

          {reflections.length > 0 && (
            <div className="mb-16">
              <SectionHead icon={Headphones} kicker="Sit with these" title="Reflections & Audio" />
              <div className="grid gap-5 sm:grid-cols-2">{reflections.map((e, i) => <EntryCard key={e.id} e={e} i={i} />)}</div>
            </div>
          )}

          <Link to="/vault/gallery" data-testid="vault-gallery-link"
            className="group relative flex items-center justify-between overflow-hidden rounded-sm p-6 sm:p-8"
            style={{ background: "#12151C", border: "1px solid rgba(212,163,115,0.14)" }}>
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "#98A898" }}>Included with your membership</p>
              <h3 className="mt-2 text-2xl sm:text-3xl" style={{ fontFamily: SERIF, color: "#F7F5F0" }}>The full Art Gallery</h3>
              <p className="mt-1.5 text-sm" style={{ color: "#A3A8B2" }}>Every somatic plate &amp; its words — unlocked for you.</p>
            </div>
            <span className="ml-4 inline-flex h-11 w-11 flex-none items-center justify-center rounded-full transition-transform group-hover:scale-110" style={{ background: "rgba(212,163,115,0.14)", color: "#D4A373" }}>
              <GalleryHorizontal className="h-5 w-5" />
            </span>
          </Link>
        </section>
      )}

      <AnimatePresence>
        {openEntry && <EntryModal e={openEntry} onClose={() => setOpenEntry(null)} />}
      </AnimatePresence>
    </div>
  );
}
